import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar';
import SectionCheckbox from '../../components/SectionCheckbox';
import Quiz from '../../components/Quiz';
import { PromptBox } from '../../components/CopyButton';
import PromptPlayground from '../../components/PromptPlayground';
import DiscordWidget from '../../components/DiscordWidget';
import '../Dashboard.css';
import './Module.css';

const Module1 = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { progress } = useProgress();
  const navigate = useNavigate();
  const [showQuiz, setShowQuiz] = useState(false);

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };
  const totalProgress = progress?.totalProgress || 0;

  // Quiz questions for Module 1
  const quizQuestions = [
    {
      question: "Qu'est-ce que l'Intelligence Artificielle ?",
      options: [
        "Un robot humanoïde",
        "Un programme capable d'apprendre et de s'améliorer",
        "Un ordinateur très rapide",
        "Un logiciel de bureautique"
      ],
      correctAnswer: 1
    },
    {
      question: "Quelle est la meilleure façon de formuler une demande à une IA ?",
      options: [
        "Être le plus vague possible",
        "Utiliser des mots techniques complexes",
        "Être précis et donner du contexte",
        "Écrire en majuscules"
      ],
      correctAnswer: 2
    },
    {
      question: "Les IA génératives comme ChatGPT sont capables de :",
      options: [
        "Remplacer complètement les humains",
        "Générer du contenu original basé sur leurs apprentissages",
        "Accéder à internet en temps réel par défaut",
        "Stocker vos conversations pour toujours"
      ],
      correctAnswer: 1
    },
    {
      question: "Pour obtenir de meilleurs résultats avec l'IA, il faut :",
      options: [
        "Poser une seule question et ne jamais itérer",
        "Itérer et affiner ses demandes progressivement",
        "Éviter de donner des exemples",
        "Toujours utiliser le même prompt"
      ],
      correctAnswer: 1
    },
    {
      question: "Quel est le principal avantage de savoir utiliser l'IA efficacement ?",
      options: [
        "Impressionner ses amis",
        "Multiplier sa productivité et ses capacités",
        "Ne plus jamais travailler",
        "Devenir développeur"
      ],
      correctAnswer: 1
    }
  ];

  // Prompt playground configuration
  const playgroundConfig = {
    title: "Créez votre premier prompt",
    description: "Personnalisez ce template pour créer un prompt efficace",
    templatePrompt: `Je suis {{role}} et j'ai besoin d'aide pour {{task}}.

Contexte : {{context}}

Ce que j'attends :
- {{expectation1}}
- {{expectation2}}

Format souhaité : {{format}}`,
    variables: [
      {
        name: 'role',
        label: 'Votre rôle/métier',
        placeholder: 'Ex: entrepreneur, étudiant, marketeur...',
        type: 'text',
        required: true
      },
      {
        name: 'task',
        label: 'La tâche à accomplir',
        placeholder: 'Ex: rédiger un email, créer un plan...',
        type: 'text',
        required: true
      },
      {
        name: 'context',
        label: 'Le contexte',
        placeholder: 'Décrivez la situation...',
        type: 'textarea',
        required: true
      },
      {
        name: 'expectation1',
        label: 'Attente n°1',
        placeholder: 'Ex: être concis',
        type: 'text',
        required: true
      },
      {
        name: 'expectation2',
        label: 'Attente n°2',
        placeholder: 'Ex: ton professionnel',
        type: 'text',
        required: true
      },
      {
        name: 'format',
        label: 'Format de réponse',
        type: 'select',
        options: [
          { value: 'texte structuré avec titres', label: 'Texte structuré' },
          { value: 'liste à puces', label: 'Liste à puces' },
          { value: 'tableau comparatif', label: 'Tableau' },
          { value: 'paragraphes courts', label: 'Paragraphes courts' }
        ],
        required: true
      }
    ]
  };

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
          <span className="logo-text">F12IA</span>
          <span className="logo-tm">™</span>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.firstName?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.firstName || 'Utilisateur'}</span>
            <span className="user-role">Membre PRO</span>
          </div>
        </div>

        <nav className="sidebar-nav-pro">
          <div className="nav-label">MENU</div>
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="nav-text">Tableau de bord</span>
          </div>

          <div className="nav-label">MODULES</div>
          <div className="nav-item active" onClick={() => navigate('/module/1')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg></span>
            <span className="nav-text">1. Introduction IA</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/2')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
            <span className="nav-text">2. ChatGPT</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/3')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span>
            <span className="nav-text">3. Claude Code</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/4')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg></span>
            <span className="nav-text">4. Automatisation</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/5')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></span>
            <span className="nav-text">5. Créer des Apps</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/6')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
            <span className="nav-text">6. Projets Avancés</span>
          </div>

          <div className="nav-label">COMPTE</div>
          <div className="nav-item" onClick={() => navigate('/profile')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg></span>
            <span className="nav-text">Mon profil</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/resources')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></span>
            <span className="nav-text">Ressources</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/certificate')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M8 14l-2 8 6-3 6 3-2-8"/></svg></span>
            <span className="nav-text">Certificat</span>
          </div>
        </nav>

        <div className="sidebar-progress-pro">
          <div className="progress-header">
            <span className="progress-title">Progression</span>
            <span className="progress-value">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar-pro">
            <div className="progress-fill-pro" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        <div className="sidebar-bottom">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
            <span className="nav-text">Déconnexion</span>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="module-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Retour aux programmes</button>
          <div className="module-badge">MODULE 1</div>
          <h1>🔍 Introduction à l'Intelligence Artificielle</h1>
          <p className="module-subtitle">Comprendre l'IA pour mieux l'utiliser</p>
          <div style={{ marginTop: '20px', maxWidth: '400px' }}>
            <ProgressBar moduleId={1} showLabel={true} size="medium" />
          </div>
        </div>

        <div className="module-content">
          {/* Section 1 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>1. Qu'est-ce que l'IA vraiment ?</h2>
              <SectionCheckbox moduleId={1} sectionId="section1" label="J'ai compris cette section" />
            </div>

            <p>
              L'Intelligence Artificielle n'est pas de la magie. C'est un <strong>programme informatique</strong> qui a été
              entraîné sur des milliards de données (textes, images, codes) pour reconnaître des patterns et générer
              des réponses cohérentes.
            </p>

            <div className="highlight-box">
              <strong>💡 Analogie simple :</strong> L'IA est comme un stagiaire ultra-compétent qui a lu tous les livres
              du monde, mais qui a besoin de vos instructions claires pour savoir exactement ce que vous voulez.
            </div>

            <h3>Les types d'IA que vous utiliserez</h3>
            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">💬</div>
                <h4>IA Conversationnelle</h4>
                <p>ChatGPT, Claude - Dialogue en langage naturel pour toutes vos questions</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🎨</div>
                <h4>IA Générative d'images</h4>
                <p>Midjourney, DALL-E - Créer des visuels à partir de descriptions</p>
              </div>
              <div className="info-card">
                <div className="card-icon">💻</div>
                <h4>IA pour le code</h4>
                <p>Claude Code, GitHub Copilot - Écrire du code à votre place</p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>2. Ce que l'IA peut (et ne peut pas) faire</h2>
              <SectionCheckbox moduleId={1} sectionId="section2" label="J'ai compris cette section" />
            </div>

            <div className="two-columns">
              <div className="column success">
                <h4>✅ L'IA excelle à :</h4>
                <ul className="module-list check">
                  <li>Rédiger des textes (emails, articles, scripts)</li>
                  <li>Résumer des documents longs</li>
                  <li>Traduire et reformuler</li>
                  <li>Générer des idées et brainstormer</li>
                  <li>Expliquer des concepts complexes</li>
                  <li>Écrire du code fonctionnel</li>
                  <li>Analyser des données</li>
                </ul>
              </div>
              <div className="column warning">
                <h4>❌ L'IA a des limites :</h4>
                <ul className="module-list cross">
                  <li>Pas d'accès à internet en temps réel (par défaut)</li>
                  <li>Peut inventer des informations ("hallucinations")</li>
                  <li>Ne remplace pas l'expertise humaine</li>
                  <li>Connaissances limitées à sa date d'entraînement</li>
                  <li>Ne comprend pas vraiment le contexte émotionnel</li>
                </ul>
              </div>
            </div>

            <div className="warning-box">
              <strong>⚠️ Important :</strong> Vérifiez toujours les informations critiques. L'IA peut se tromper avec
              beaucoup d'assurance. Utilisez-la comme un assistant, pas comme une source de vérité absolue.
            </div>
          </section>

          {/* Section 3 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>3. La clé : savoir parler à l'IA</h2>
              <SectionCheckbox moduleId={1} sectionId="section3" label="J'ai compris cette section" />
            </div>

            <p>
              La différence entre quelqu'un qui "n'arrive pas à utiliser l'IA" et un expert n'est pas l'intelligence —
              c'est la façon de formuler les demandes. On appelle ça le <strong>prompting</strong>.
            </p>

            <h3>Exemple concret : la différence d'un bon prompt</h3>

            <PromptBox title="❌ Mauvais prompt">
{`Écris-moi un email.`}
            </PromptBox>

            <PromptBox title="✅ Bon prompt">
{`Écris un email professionnel pour relancer un prospect B2B qui n'a pas répondu depuis 2 semaines.

Contexte : Je suis consultant en marketing digital, le prospect est une PME de 50 employés dans l'e-commerce.

Ton : professionnel mais chaleureux, pas agressif.

Longueur : court (max 100 mots).

Inclure : une proposition de call de 15 minutes et un élément de valeur (astuce ou stat intéressante).`}
            </PromptBox>

            <div className="tip-box">
              <div className="tip-icon">💡</div>
              <div className="tip-content">
                <h4>La règle d'or</h4>
                <p>Plus vous donnez de contexte et de précisions, meilleure sera la réponse. Imaginez que vous
                   briefez un nouveau collaborateur qui ne connaît rien de votre situation.</p>
              </div>
            </div>
          </section>

          {/* Section 4 - Interactive Playground */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>4. Pratiquez : créez votre premier prompt</h2>
              <SectionCheckbox moduleId={1} sectionId="section4" label="J'ai fait l'exercice" />
            </div>

            <p>
              Utilisez cet outil interactif pour créer votre premier prompt structuré.
              Remplissez les champs, générez le prompt, puis copiez-le pour l'utiliser dans ChatGPT ou Claude.
            </p>

            <PromptPlayground
              title={playgroundConfig.title}
              description={playgroundConfig.description}
              templatePrompt={playgroundConfig.templatePrompt}
              variables={playgroundConfig.variables}
            />
          </section>

          {/* Section 5 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>5. Les principes fondamentaux du prompting</h2>
              <SectionCheckbox moduleId={1} sectionId="section5" label="J'ai compris cette section" />
            </div>

            <div className="principles-grid">
              <div className="principle-card">
                <div className="principle-number">1</div>
                <h4>Contexte</h4>
                <p>Qui êtes-vous ? Quelle est la situation ?</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">2</div>
                <h4>Rôle</h4>
                <p>Quel rôle doit jouer l'IA ? Expert, conseiller, rédacteur ?</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">3</div>
                <h4>Instructions</h4>
                <p>Que voulez-vous exactement ? Soyez précis.</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">4</div>
                <h4>Format</h4>
                <p>Comment voulez-vous la réponse ? Liste, paragraphe, tableau ?</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">5</div>
                <h4>Exemples</h4>
                <p>Donnez des exemples de ce que vous voulez (ou ne voulez pas).</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">6</div>
                <h4>Itération</h4>
                <p>Affinez la réponse en demandant des modifications.</p>
              </div>
            </div>
          </section>

          {/* Summary */}
          <section className="module-section">
            <h2>📝 Résumé du module</h2>
            <div className="summary-box">
              <ul>
                <li><strong>L'IA est un outil</strong> — Pas magique, mais extrêmement puissant quand bien utilisé</li>
                <li><strong>La qualité du prompt = la qualité du résultat</strong> — Investissez du temps dans vos demandes</li>
                <li><strong>Contexte + Précision + Format</strong> — Les 3 piliers d'un bon prompt</li>
                <li><strong>Itérez</strong> — Ne vous arrêtez pas à la première réponse, affinez</li>
                <li><strong>Vérifiez</strong> — L'IA peut se tromper, gardez votre esprit critique</li>
              </ul>
            </div>
          </section>

          {/* Quiz Section */}
          <section className="module-section">
            <h2>📝 Quiz de validation</h2>
            <p>Testez vos connaissances pour valider ce module. Vous devez obtenir au moins 70% pour passer au suivant.</p>

            {!showQuiz ? (
              <button
                className="btn btn-primary"
                onClick={() => setShowQuiz(true)}
                style={{ marginTop: '16px' }}
              >
                🎯 Commencer le quiz
              </button>
            ) : (
              <Quiz
                moduleId={1}
                questions={quizQuestions}
                onComplete={(score) => {
                  if (score >= 70) {
                    // Module completed!
                  }
                }}
              />
            )}
          </section>

          {/* Community */}
          <section className="module-section">
            <h2>💬 Besoin d'aide ?</h2>
            <p>Rejoignez notre communauté Discord pour poser vos questions et échanger avec les autres apprenants.</p>
            <DiscordWidget inviteLink="https://discord.gg/votre-lien" />
          </section>

          <div className="module-navigation">
            <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>← Retour au Dashboard</button>
            <button className="btn btn-primary" onClick={() => navigate('/module/2')}>Module suivant : ChatGPT →</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Module1;
